import { Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges } from '@angular/core';
import { fromEvent as observableFromEvent } from 'rxjs';
import { AccessControlGateDto } from '@shared/src/dtos/accesscontrol/gate/AccessControlGateDto';
import { AccessControlGatePlateDto } from '@shared/src/dtos/accesscontrol/gate/plate/AccessControlGatePlateDto';
import { ForkliftDto } from '@shared/src/dtos/accesscontrol/forklift/ForkliftDto';
import { OptionDto } from '@shared/src/dtos/maintenance/option/OptionDto';
import { OptionsStripDto } from '@shared/src/dtos/maintenance/option/OptionsStripDto';
import { DragDropService } from '@shared/src/services/dragdrop/DragDropService';

@Component({
    selector: 'shared-forklift-left',
    templateUrl: './shared.forklift.left.component.html',
    styleUrls: ['./shared.forklift.left.component.scss']
})
export class SharedForkliftLeftComponent implements OnChanges {

    constructor(private ele: ElementRef, public dragDropService: DragDropService, private ngZone: NgZone) {
        this.ngZone.runOutsideAngular(() => {
            observableFromEvent(ele.nativeElement, "dragover")
                .subscribe((event: DragEvent) => {
                    this.dragDropService.onDragOver(null, null, event);
                });
        });
    }

    @Input() public value: ForkliftDto | null = null;
    @Input() public columnLeftView: number = 0;
    @Input() public openGateIds: string[] = [];
    @Input() public searchText: string | null = null;
    @Input() public selectedGateId: string = '';
    @Input() public selectedRoute: AccessControlGatePlateDto | null = null;
    @Output() public clickGateOption = new EventEmitter<OptionDto>();
    @Output() public clickRoute = new EventEmitter<AccessControlGatePlateDto>();
    @Output() public dblClickRoute = new EventEmitter<AccessControlGatePlateDto>();
    @Output() public changeOpenGateIds = new EventEmitter<string[]>();
    @Output() public refresh = new EventEmitter<boolean>();

    public gateOptions: OptionsStripDto | null = null;

    ngOnChanges(changes: SimpleChanges) {
        if (changes['value'] || changes['selectedGateId']) {
            this.buildGateOptions();
        }
        if (changes['columnLeftView']) {
            const prev: number = changes['columnLeftView'].previousValue;
            const curr: number = changes['columnLeftView'].currentValue;
            if (prev > 0 && curr === 0) {
                this.autoSelectGateForRoute();
            }
        }
    }

    private autoSelectGateForRoute() {
        if (this.selectedRoute == null) return;
        const gates = this.value?.accessControlDTO?.accessControlGates ?? [];
        const gateIndex = gates.findIndex(g => g.plates?.some(p => p.routeId === this.selectedRoute.routeId));
        if (gateIndex === -1) return;
        const gate = gates[gateIndex];
        const option = OptionDto.build(gate.gateId.toString(), gate.gateName, '', '', (gateIndex + 1) * 10, -1, -1, true, false);
        option.backColor = gate.backColor ?? null;
        option.foreColor = gate.foreColor ?? null;
        this.clickGateOption.emit(option);
    }

    private buildGateOptions() {
        const gates: AccessControlGateDto[] = this.value?.accessControlDTO?.accessControlGates ?? [];
        this.gateOptions = new OptionsStripDto();
        gates.forEach((gate, i) => {
            const selected = gate.gateId.toString() === this.selectedGateId;
            const option = OptionDto.build(gate.gateId.toString(), gate.gateName, '', '', (i + 1) * 10, -1, -1, selected, false);
            option.backColor = gate.backColor ?? null;
            option.foreColor = gate.foreColor ?? null;
            this.gateOptions.add(option);
        });
        OptionsStripDto.arrange(this.gateOptions, this.selectedGateId, '');
    }

    toggleGate(gate: AccessControlGateDto) {
        const id = gate.gateId.toString();
        const allIds = this.gates.map(g => g.gateId.toString());
        const currentOpen = this.openGateIds?.length > 0 ? this.openGateIds : allIds;
        const newIds = currentOpen.includes(id)
            ? currentOpen.filter(i => i !== id)
            : [...currentOpen, id];
        this.changeOpenGateIds.emit(newIds);
    }

    get gates(): AccessControlGateDto[] {
        return this.value?.accessControlDTO?.accessControlGates ?? [];
    }

    get selectedGatePlates(): AccessControlGatePlateDto[] {
        const gate = this.gates.find(g => g.gateId.toString() === this.selectedGateId);
        return gate?.plates ?? [];
    }

    get selectedGate(): AccessControlGateDto {
        return this.gates.find(g => g.gateId.toString() === this.selectedGateId);
    }

    get openGates(): AccessControlGateDto[] {
        if (!this.openGateIds || this.openGateIds.length === 0)
            return this.gates;
        return this.gates.filter(g => this.openGateIds.includes(g.gateId.toString()));
    }

    get closedGates(): AccessControlGateDto[] {
        if (!this.openGateIds || this.openGateIds.length === 0)
            return [];
        return this.gates.filter(g => !this.openGateIds.includes(g.gateId.toString()));
    }

    drop(event: DragEvent, gate: AccessControlGateDto) {
        if (!gate) return;
        this.dragDropService.onDrop(
            DragDropService.extractDraggedObject(event),
            DragDropService.createDropObject(gate, DragDropService.HARDMAN_ACCESSCONTROLGATEDTO),
            event,
            this.onDropCallback,
            this
        );
    }

    onDropCallback(draggedobject: any, ondropobject: any, event: DragEvent, hardmanobjectdragged: any, hardmanobjectdropped: any, callbackparam: SharedForkliftLeftComponent, result: any) {
        if (result && callbackparam)
            callbackparam.refresh.emit(true);
    }

    public pretty(value: any): string {
        return JSON.stringify(value);
    }
}
